/**
 * ResourceSummaryCards — 5 KPI tiles for the Resources view.
 *
 * 1. FTE Demand — total FTEs needed from total investment
 * 2. FTE Supply — available FTEs (IWT + IWT South + Other)
 * 3. Resource Gap — surplus/shortfall indicator
 * 4. Top Consumer — project with highest FTE allocation
 * 5. Resource Utilization — horizontal gauge bar with breakdown
 */

import { UserPlus, Users, AlertTriangle, CheckCircle, Target, Activity } from 'lucide-react';
import type { ResourceConfig, ResourceMetrics } from '../../types/resource-config';
import { formatCompactCurrency } from '../../utils/financial-calculations';

interface Props {
  resourceConfig: ResourceConfig;
  metrics: ResourceMetrics;
  topConsumer: { name: string; fte: number; pctOfDemand: number } | null;
}

export function ResourceSummaryCards({ resourceConfig, metrics, topConsumer }: Props) {
  const utilPct = metrics.totalAvailableFtes > 0
    ? (metrics.ftesNeeded / metrics.totalAvailableFtes) * 100
    : 0;
  const clampedPct = Math.min(utilPct, 100);
  const gaugeColor = utilPct <= 70 ? '#22c55e' : utilPct <= 90 ? '#f59e0b' : '#ef4444';

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
      {/* 1. FTE Demand */}
      <KpiCard
        icon={UserPlus}
        iconColor="#3b82f6"
        title="FTE Demand"
        value={`${metrics.ftesNeeded.toFixed(1)} FTE`}
        subtitle={`From ${formatCompactCurrency(metrics.totalInvestment)} total investment`}
      />

      {/* 2. FTE Supply */}
      <KpiCard
        icon={Users}
        iconColor="#6366f1"
        title="FTE Supply"
        value={`${metrics.totalAvailableFtes.toFixed(1)} FTE`}
        subtitle={`IWT ${resourceConfig.iwtResources.toFixed(1)} · South ${metrics.iwtSouthFtes.toFixed(1)} · Other ${metrics.otherSubcontractorFtes.toFixed(1)}`}
      />

      {/* 3. Resource Gap */}
      <KpiCard
        icon={metrics.fteDelta >= 0 ? CheckCircle : AlertTriangle}
        iconColor={metrics.fteDelta >= 0 ? '#22c55e' : '#ef4444'}
        title="Resource Gap"
        value={`${metrics.fteDelta >= 0 ? '+' : ''}${metrics.fteDelta.toFixed(1)} FTE`}
        subtitle={`${utilPct.toFixed(0)}% utilization — ${metrics.fteDelta >= 0 ? 'surplus' : 'shortfall'}`}
        valueColor={metrics.fteDelta >= 0 ? '#22c55e' : '#ef4444'}
      />

      {/* 4. Utilization gauge */}
      <div className="glass-panel p-4 rounded-lg flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: gaugeColor + '20' }}
        >
          <Activity size={20} style={{ color: gaugeColor }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-medium mb-0.5" style={{ color: 'var(--text-muted)' }}>
            Utilization
          </div>
          <div className="text-xl font-bold tabular-nums" style={{ color: gaugeColor }}>
            {utilPct.toFixed(0)}%
          </div>
          {/* Mini gauge bar */}
          <div className="w-full h-1.5 rounded-full mt-0.5 overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${clampedPct}%`, background: gaugeColor }}
            />
          </div>
          <div className="text-xs mt-0.5 truncate" style={{ color: 'var(--text-dim)' }}>
            IWT {resourceConfig.iwtResources.toFixed(1)} · South {metrics.iwtSouthFtes.toFixed(1)} · Other {metrics.otherSubcontractorFtes.toFixed(1)}
          </div>
        </div>
      </div>

      {/* 5. Top Consumer */}
      <KpiCard
        icon={Target}
        iconColor="#f59e0b"
        title="Top Consumer"
        value={topConsumer ? (topConsumer.name.length > 20 ? topConsumer.name.slice(0, 17) + '…' : topConsumer.name) : '—'}
        subtitle={topConsumer ? `${topConsumer.fte.toFixed(1)} FTE (${topConsumer.pctOfDemand.toFixed(0)}% of demand)` : 'No project data'}
      />
    </div>
  );
}

function KpiCard({
  icon: Icon,
  iconColor,
  title,
  value,
  subtitle,
  valueColor,
}: {
  icon: typeof UserPlus;
  iconColor: string;
  title: string;
  value: string;
  subtitle: string;
  valueColor?: string;
}) {
  return (
    <div className="glass-panel p-4 rounded-lg flex items-start gap-3">
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
        style={{ background: iconColor + '20' }}
      >
        <Icon size={20} style={{ color: iconColor }} />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] font-medium mb-0.5" style={{ color: 'var(--text-muted)' }}>
          {title}
        </div>
        <div
          className="text-xl font-bold tabular-nums truncate"
          style={{ color: valueColor ?? 'var(--text-primary)' }}
        >
          {value}
        </div>
        <div className="text-xs mt-0.5 truncate" style={{ color: 'var(--text-dim)' }}>
          {subtitle}
        </div>
      </div>
    </div>
  );
}
